/* =====================================================================
 * specializedStorageSlot — host 가 specialized 에이전트의 storage UI 를
 * 주입하는 DI slot.
 *
 * StoragePage 좌측 사이드(StorageSidebar) 의 "전문 에이전트" 섹션 안에는
 * 호스트가 등록한 트리 컴포넌트가 렌더되며(매니페스트의 specialized 에이전트
 * id 마다 1 개), 메인 영역에는 같은 매핑의 리스트 컴포넌트가 렌더된다.
 *
 * shared 패키지(`@auw/workspace-ui`) 는 host 의 도메인 store(cadAgentStore)
 * 를 직접 import 할 수 없으므로 DI 로 처리. 호스트(`askyourai/main.tsx`)
 * 가 bootstrap 시점에 `setSpecializedStorageComponents('cad', {...})` 로
 * 등록한다. 매핑 없는 id 는 storage 측에서 안내 fallback.
 *
 * Tree / List 양쪽 모두 `SpecializedStorageProps` 를 받아 동일한 selection
 * 상태를 공유한다 — 사용자가 좌측 트리에서 폴더를 클릭하면 메인 리스트가
 * 즉시 그 폴더 내 파일로 갱신된다.
 * ===================================================================== */

import type React from 'react';
import type { SidebarSelection } from '../components/storage/StorageSidebar';

export interface PickedStorageFile {
  readonly fileId: string;
  readonly name: string;
  readonly mimeType: string;
  readonly size: number;
}

export interface SpecializedStorageProps {
  /** 현재 storage 의 선택 상태 (모든 섹션 공통). */
  readonly selection: SidebarSelection;
  /** 트리에서 사용자가 노드를 클릭하면 호출. */
  readonly onSelect: (next: SidebarSelection) => void;
  /** 파일 첨부 선택기에서만 주입되는 선택 변경 콜백. */
  readonly onPickedFilesChange?: (files: readonly PickedStorageFile[]) => void;
}

export interface SpecializedStorageComponents {
  /** 사이드바의 "전문 에이전트 > <id>" 노드 안에 렌더되는 트리. */
  readonly Tree: React.ComponentType<SpecializedStorageProps>;
  /** StoragePage 메인 영역에서 specialized 선택일 때 렌더되는 파일 리스트. */
  readonly List: React.ComponentType<SpecializedStorageProps>;
}

const _registry = new Map<string, SpecializedStorageComponents>();

export function setSpecializedStorageComponents(
  id: string,
  comps: SpecializedStorageComponents | null,
): void {
  if (comps) _registry.set(id, comps);
  else _registry.delete(id);
}

export function getSpecializedStorageComponents(
  id: string,
): SpecializedStorageComponents | null {
  return _registry.get(id) ?? null;
}
